import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import dayjs from 'dayjs';
import { Department } from 'src/app/models/department.model';
import { PartnerContract } from 'src/app/models/partner-contract.model';
import { ApiService } from 'src/app/services/api.service';

export interface PartnerContractDialogData {
  user_id: number;
  departments: Department[];
}

@Component({
  selector: 'app-partner-contract-dialog',
  templateUrl: './partner-contract-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
  ],
})
export class PartnerContractDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<PartnerContractDialogComponent, PartnerContract | null>);
  private readonly data = inject<PartnerContractDialogData>(MAT_DIALOG_DATA);
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  private readonly destroy_ref = inject(DestroyRef);

  readonly departments = signal(this.data.departments);
  readonly saving = signal(false);

  readonly form = this.fb.group({
    start_date: [null as Date | null, Validators.required],
    end_date: [null as Date | null],
    department_ids: [[] as number[]],
  });

  ngOnInit(): void {
    if (this.departments().length === 0) {
      this.api
        .getDepartments()
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe(response => {
          this.departments.set(response.data ?? []);
        });
    }
  }

  cancel(): void {
    this.dialog_ref.close(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.saving.set(true);
    this.api
      .createUserPartnerContract(this.data.user_id, {
        start_date: dayjs(value.start_date).format('YYYY-MM-DD'),
        end_date: value.end_date ? dayjs(value.end_date).format('YYYY-MM-DD') : null,
        department_ids: value.department_ids ?? [],
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: contract => {
          this.saving.set(false);
          this.dialog_ref.close(contract);
        },
        error: () => this.saving.set(false),
      });
  }
}
